/**
 * Hook to handle session token exchange from OAuth callback.
 *
 * @description
 * This hook automatically detects an `auth=pending` marker query parameter in the URL
 * (set by the OAuth callback redirect), and exchanges the session for Supabase tokens.
 * The actual encrypted session payload never appears in the URL or in JS-visible state —
 * it rides in an httpOnly cookie the browser sends automatically on the (same-origin)
 * POST below. The marker is removed from the URL after processing (success or failure).
 *
 * The hook is idempotent - it will only process a token once, even if called
 * multiple times. The `isExchanging` state prevents concurrent exchanges.
 *
 * @returns {Object} Hook return value
 * @returns {boolean} returns.isExchanging - Whether token exchange is in progress
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isExchanging } = useSessionToken();
 *   if (isExchanging) return <Loading />;
 *   return <Content />;
 * }
 * ```
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { logError } from '@/lib/utils';
import { removeQueryParam } from '@/lib/utils/url';
import {
  PENDING_SESSION_MARKER_PARAM,
  PENDING_SESSION_MARKER_VALUE,
} from '@/lib/auth/session-marker';

/**
 * Check if the pending-session marker is present in the URL synchronously.
 * Used to initialize state before any effects run.
 */
function hasPendingSessionMarkerInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(PENDING_SESSION_MARKER_PARAM) === PENDING_SESSION_MARKER_VALUE;
}

export function useSessionToken() {
  const searchParams = useSearchParams();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  // Initialize to true if the marker is present in URL to prevent race conditions
  // where API calls start before the token exchange completes. We check the URL
  // directly (not searchParams) to ensure synchronous detection on first render.
  const [isExchanging, setIsExchanging] = useState(hasPendingSessionMarkerInUrl);
  // Track if we've started exchanging to prevent double-exchange
  const hasStartedExchange = useRef(false);

  const exchangeToken = useCallback(async () => {
    /**
     * Attempt a single POST to /api/auth/session and return the parsed response.
     * The encrypted session payload lives in an httpOnly `pending_session` cookie set
     * by the OAuth callback — the browser sends it automatically on this same-origin
     * request, so no body/token value is needed (or even visible to this JS).
     * Throws on network errors, server errors, or invalid responses.
     */
    const attemptExchange = async (): Promise<{
      access_token: string;
      refresh_token: string;
    }> => {
      const response = await fetch('/api/auth/session', {
        method: 'POST',
      });

      if (!response.ok) {
        // Try to parse error response, but handle JSON parse failures gracefully
        let errorData: { detail?: string } = { detail: 'Unknown error' };
        try {
          const responseText = await response.text();
          if (responseText && responseText.trim()) {
            try {
              errorData = JSON.parse(responseText);
            } catch {
              // If JSON parse fails, use the raw text if it looks like an error message
              const isHtml = responseText.trim().startsWith('<');
              if (!isHtml && responseText.length < 200) {
                errorData = { detail: responseText };
              } else {
                errorData = {
                  detail: `Server error (${response.status} ${response.statusText})`,
                };
              }
            }
          } else {
            errorData = { detail: `Server error (${response.status} ${response.statusText})` };
          }
        } catch {
          // If we can't even read the response, use status text
          errorData = { detail: `Server error (${response.status} ${response.statusText})` };
        }
        const err = new Error(errorData.detail || 'Failed to exchange token');
        // Attach status so caller can decide whether to retry
        (err as Error & { status?: number }).status = response.status;
        throw err;
      }

      // Parse response JSON with error handling
      let responseData: { access_token?: string; refresh_token?: string };
      try {
        const responseText = await response.text();
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        logError(
          'Failed to parse session token response',
          {
            status: response.status,
            statusText: response.statusText,
          },
          parseError
        );
        throw new Error(
          `Invalid response from server. This may indicate a server error or expired session. Please try again.`
        );
      }

      const { access_token, refresh_token } = responseData;

      if (!access_token || !refresh_token) {
        throw new Error('Missing tokens in response');
      }

      return { access_token, refresh_token };
    };

    try {
      // Retry once on network failures (TypeError from fetch).
      // Server errors (4xx/5xx) and data errors (missing tokens, parse failures)
      // are NOT retried since they indicate non-transient problems.
      // Real fetch() network failures throw TypeError (e.g., "Failed to fetch").
      const MAX_RETRIES = 1;
      const RETRY_DELAY_MS = 1500;
      let lastError: Error | null = null;
      let tokens: { access_token: string; refresh_token: string } | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          tokens = await attemptExchange();
          break; // Success
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          // Only retry on actual network failures (TypeError from fetch).
          // Server responses (even 5xx) and data errors are not retried.
          const isNetworkError = lastError instanceof TypeError;

          if (isNetworkError && attempt < MAX_RETRIES) {
            logError('Session token exchange network error, retrying', {
              attempt: attempt + 1,
              maxRetries: MAX_RETRIES,
              error: lastError.message,
            });
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }
          break; // Not retryable or out of retries
        }
      }

      if (!tokens) {
        throw lastError || new Error('Failed to exchange token');
      }

      const { access_token, refresh_token } = tokens;

      // Store token in localStorage (for API client)
      localStorage.setItem('sb-access-token', access_token);

      // Set Supabase session
      const { supabase } = await import('@/lib/supabase');
      await supabase.auth.setSession({
        access_token,
        refresh_token,
      });

      // Refresh user data
      await refreshUser();

      // Remove the pending-session marker from URL
      removeQueryParam(PENDING_SESSION_MARKER_PARAM);

      toast({
        title: 'Success',
        description: 'You have been signed in successfully.',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete sign in';
      logError('Error exchanging session token', {}, error);

      // Remove the pending-session marker from URL even on error
      removeQueryParam(PENDING_SESSION_MARKER_PARAM);

      toast({
        title: 'Authentication Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsExchanging(false);
    }
  }, [refreshUser, toast]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Use ref to prevent double-exchange (can't rely on state since we initialize to true)
    if (hasStartedExchange.current) return;

    const hasMarker =
      searchParams.get(PENDING_SESSION_MARKER_PARAM) === PENDING_SESSION_MARKER_VALUE;
    if (!hasMarker) {
      // No marker from searchParams, but check if it's actually in the URL.
      // useSearchParams() may not be hydrated yet on first render, so
      // window.location.search is the ground truth. If the marker IS in the URL
      // but searchParams doesn't have it yet, keep isExchanging true and wait
      // for searchParams to hydrate (it's in the dependency array).
      if (isExchanging && !hasPendingSessionMarkerInUrl()) {
        setIsExchanging(false);
      }
      return;
    }

    hasStartedExchange.current = true;
    setIsExchanging(true);
    exchangeToken();
  }, [searchParams, isExchanging, exchangeToken]);

  return { isExchanging };
}
